import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Users, FileText, Settings, LifeBuoy, Star, Shield, Plus, TrendingUp, CheckCircle2, Download } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Field, Input, Select, Spinner, Reveal } from '../components/ui';
import api, { vnd } from '../api';

const TABS = [
  { k: 'overview', label: 'Overview', icon: LayoutDashboard },
  { k: 'customers', label: 'Customers', icon: Users },
  { k: 'policies', label: 'Policies', icon: FileText },
  { k: 'config', label: 'Configuration', icon: Settings },
  { k: 'tickets', label: 'Tickets', icon: LifeBuoy },
  { k: 'feedback', label: 'Feedback', icon: Star },
  { k: 'ops', label: 'Ops Users', icon: Shield },
];

const Stat = ({ label, value, sub }) => (
  <Card className="p-5">
    <div className="kicker">{label}</div>
    <div className="font-display text-2xl font-extrabold mt-1 text-jade">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </Card>
);

export default function Admin() {
  const [tab, setTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [config, setConfig] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', role: 'adjuster' });
  const [cfgForm, setCfgForm] = useState({ amt: 20000000, conf: 0.75 });
  const [msg, setMsg] = useState('');

  const load = {
    overview: () => api.get('/admin/metrics').then((r) => setMetrics(r.data)),
    customers: () => api.get('/admin/users').then((r) => setUsers(r.data)),
    policies: () => api.get('/admin/policies').then((r) => setPolicies(r.data)),
    config: () => api.get('/admin/config').then((r) => { setConfig(r.data); setCfgForm({ amt: r.data.decision_config.auto_approve_max_amount_vnd, conf: r.data.decision_config.min_ai_confidence }); }),
    tickets: () => api.get('/tickets').then((r) => setTickets(r.data)),
    feedback: () => api.get('/feedback').then((r) => setFeedback(r.data)),
    ops: () => api.get('/admin/users').then((r) => setUsers(r.data)),
  };
  useEffect(() => { (load[tab] || (() => {}))().catch(() => {}); /* eslint-disable-next-line */ }, [tab]);

  const createUser = async (e) => {
    e.preventDefault(); setMsg('');
    try { await api.post('/admin/users', form); setMsg('User created (password Tasco@123)'); setForm({ email: '', name: '', role: form.role }); load.ops(); }
    catch (e2) { setMsg(e2.response?.data?.detail || 'Error'); }
  };

  const saveConfig = async () => {
    setMsg('');
    await api.post('/admin/config', { auto_approve_max_amount_vnd: Number(cfgForm.amt), min_ai_confidence: Number(cfgForm.conf) });
    setMsg('Configuration saved'); load.config();
  };

  const updateTicket = async (id, status) => { await api.post(`/tickets/${id}/update`, { status }); load.tickets(); };

  const [exporting, setExporting] = useState(false);
  const exportFinance = async () => {
    setExporting(true);
    try {
      const r = await api.get('/admin/finance/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasco-finance-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const customers = users.filter((u) => ['customer', 'fleet_operator'].includes(u.role));
  const opsUsers = users.filter((u) => ['adjuster', 'admin'].includes(u.role));

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="admin-page">
      <Reveal>
        <div className="kicker">ADMINISTRATOR</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Command Center</h1>
      </Reveal>

      <div className="grid lg:grid-cols-6 gap-5 mt-8">
        <div className="lg:col-span-1 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`admin-tab-${tb.k}`}
              className={`px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 whitespace-nowrap ${tab === tb.k ? 'bg-jade/10 text-jade' : 'text-slate-400 hover:text-white'}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        <div className="lg:col-span-5">
          {msg && <div className="mb-4 text-jade text-sm" data-testid="admin-msg">{msg}</div>}

          {tab === 'overview' && (metrics ? (
            <div className="space-y-5" data-testid="admin-overview">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="kicker flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Finance & operations</div>
                <Button variant="ghost" onClick={exportFinance} disabled={exporting} data-testid="admin-finance-export" className="text-sm">
                  <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Customers" value={metrics.customers} />
                <Stat label="Ops users" value={metrics.ops_users} />
                <Stat label="Active policies" value={metrics.active_policies} sub={`${metrics.total_policies} total`} />
                <Stat label="Premium collected" value={vnd(metrics.premium_collected_vnd)} />
                <Stat label="Renewals due" value={metrics.renewals_due} />
                <Stat label="Settlements paid" value={vnd(metrics.settlements_paid_vnd)} />
                <Stat label="Open tickets" value={metrics.open_tickets} />
                <Stat label="Feedback" value={metrics.feedback_count} />
              </div>
              <Card className="p-5">
                <div className="kicker mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Claims by status</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(metrics.claims_by_status).map(([k, v]) => (
                    <Badge key={k} tone={k === 'escalated' ? 'amber' : k === 'approved' ? 'jade' : 'slate'}>{k}: {v}</Badge>
                  ))}
                </div>
              </Card>
            </div>
          ) : <Spinner label="…" />)}

          {tab === 'customers' && (
            <Card className="p-5" data-testid="admin-customers">
              <div className="kicker mb-3">Customers & Fleet operators ({customers.length})</div>
              <div className="space-y-2">
                {customers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                    <div><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                    <Badge tone="slate">{u.role}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {tab === 'policies' && (
            <Card className="p-5" data-testid="admin-policies">
              <div className="kicker mb-3">All policies ({policies.length})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] font-mono uppercase text-slate-500 border-b border-white/8">
                    <th className="py-2 pr-3">Policy</th><th className="py-2 pr-3">Vehicle</th><th className="py-2 pr-3">Renewal</th><th className="py-2 text-right">Premium</th></tr></thead>
                  <tbody>
                    {policies.slice(0, 40).map((p) => (
                      <tr key={p.id} className="border-b border-white/5">
                        <td className="py-2 pr-3 font-mono text-xs">{p.policy_no}</td>
                        <td className="py-2 pr-3">{p.vehicle?.make} {p.vehicle?.model}</td>
                        <td className="py-2 pr-3"><span className={p.renewal?.due ? 'text-amber' : 'text-slate-400'}>{p.end_date?.slice(0, 10)}</span></td>
                        <td className="py-2 text-right font-mono">{vnd(p.premium_vnd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {tab === 'config' && config && (
            <div className="space-y-4" data-testid="admin-config">
              <Card className="p-5">
                <div className="kicker mb-3">Decision engine thresholds</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Auto-approve max amount (VND)"><Input data-testid="cfg-amount" type="number" value={cfgForm.amt} onChange={(e) => setCfgForm({ ...cfgForm, amt: e.target.value })} /></Field>
                  <Field label="Min AI confidence (0-1)"><Input data-testid="cfg-conf" type="number" step="0.05" value={cfgForm.conf} onChange={(e) => setCfgForm({ ...cfgForm, conf: e.target.value })} /></Field>
                </div>
                <Button data-testid="cfg-save" onClick={saveConfig} className="mt-4">Save configuration</Button>
              </Card>
              <Card className="p-5">
                <div className="kicker mb-3">Products ({config.products.length})</div>
                <div className="space-y-1">
                  {config.products.map((p) => (
                    <div key={p.code} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5">
                      <span>{p.name_en}</span>
                      <Badge tone={p.is_mandatory ? 'jade' : 'slate'}>{p.is_mandatory ? 'mandatory' : 'optional'}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {tab === 'tickets' && (
            <Card className="p-5" data-testid="admin-tickets">
              <div className="kicker mb-3">Support tickets ({tickets.length})</div>
              {tickets.length === 0 && <div className="text-slate-400 text-sm">No tickets.</div>}
              <div className="space-y-2">
                {tickets.map((tk) => (
                  <div key={tk.id} className="card p-3" data-testid={`ticket-${tk.ticket_no}`}>
                    <div className="flex items-center justify-between">
                      <div><span className="font-mono text-xs">{tk.ticket_no}</span> · <span className="text-sm font-medium">{tk.subject}</span></div>
                      <Badge tone={tk.status === 'open' ? 'amber' : 'jade'}>{tk.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{tk.message}</div>
                    <div className="text-[10px] text-slate-600 mt-1">{tk.owner_name} · {tk.category}</div>
                    {tk.status === 'open' && <Button data-testid={`ticket-resolve-${tk.ticket_no}`} onClick={() => updateTicket(tk.id, 'resolved')} className="mt-2 !py-1.5 text-xs">Mark resolved</Button>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {tab === 'feedback' && (
            <Card className="p-5" data-testid="admin-feedback">
              <div className="kicker mb-3">Customer feedback ({feedback.length})</div>
              {feedback.length === 0 && <div className="text-slate-400 text-sm">No feedback yet.</div>}
              <div className="space-y-2">
                {feedback.map((f) => (
                  <div key={f.id} className="card p-3 flex items-start justify-between">
                    <div><div className="text-sm">{f.message || '—'}</div><div className="text-[10px] text-slate-600 mt-1">{f.owner_name} · {f.category}</div></div>
                    <div className="flex gap-0.5">{Array.from({ length: f.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-amber fill-amber" />)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {tab === 'ops' && (
            <div className="space-y-4" data-testid="admin-ops-users">
              <Card className="p-5">
                <div className="kicker mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Create user</div>
                <form onSubmit={createUser} className="grid sm:grid-cols-4 gap-3 items-end">
                  <Field label="Name"><Input data-testid="newuser-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                  <Field label="Email"><Input data-testid="newuser-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                  <Field label="Role">
                    <Select data-testid="newuser-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                      <option value="adjuster">adjuster</option><option value="admin">admin</option>
                      <option value="customer">customer</option><option value="fleet_operator">fleet_operator</option>
                    </Select>
                  </Field>
                  <Button type="submit" data-testid="newuser-submit">Create</Button>
                </form>
              </Card>
              <Card className="p-5">
                <div className="kicker mb-3">Ops team ({opsUsers.length})</div>
                <div className="space-y-2">
                  {opsUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                      <div><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                      <Badge tone={u.role === 'admin' ? 'jade' : 'slate'}>{u.role}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
